import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface Heatmap24Props {
  /** 24-element array (one per hour, 0..23), values in any scale; rescaled internally. */
  hours: number[] | undefined;
  /** Hours the user is currently considered "in window" — shown as ring markers. */
  activeHours?: number[];
  className?: string;
  colorVar?: string; // tailwind hsl(var(--primary)) by default
}

export function Heatmap24Hour({
  hours,
  activeHours,
  className,
  colorVar = 'hsl(var(--primary))',
}: Heatmap24Props) {
  if (!hours || hours.length !== 24) {
    return (
      <div className={cn('flex h-24 items-center justify-center rounded-md border border-dashed text-xs text-muted-foreground', className)}>
        No engagement histogram yet
      </div>
    );
  }
  const max = Math.max(1, ...hours);
  const activeSet = new Set(activeHours ?? []);

  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="grid grid-cols-24 gap-0.5" style={{ gridTemplateColumns: 'repeat(24, minmax(0, 1fr))' }}>
        {hours.map((v, i) => {
          const intensity = v / max;
          const isActive = activeSet.has(i);
          return (
            <Tooltip key={i}>
              <TooltipTrigger asChild>
                <div
                  aria-label={`hour ${i}: ${v}`}
                  className={cn(
                    'relative h-10 rounded-sm border',
                    isActive ? 'border-primary' : 'border-transparent',
                  )}
                  style={{ backgroundColor: colorVar, opacity: Math.max(0.08, intensity) }}
                />
              </TooltipTrigger>
              <TooltipContent>
                {`${String(i).padStart(2, '0')}:00 — ${v.toFixed(2)}`}
                {isActive && <div className="text-[10px] text-primary">in window</div>}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
      <div className="grid grid-cols-8 text-[10px] text-muted-foreground" style={{ gridTemplateColumns: 'repeat(8, minmax(0, 1fr))' }}>
        {[0, 3, 6, 9, 12, 15, 18, 21].map((h) => (
          <div key={h}>{String(h).padStart(2, '0')}</div>
        ))}
      </div>
    </div>
  );
}
